import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Alert } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import api, { apiDanizinha } from '../../services/api';
import config from '../../services/config';
import useNavigationHandler from '../../hooks/useNavigationHandler';
import * as S from './styled.jsx';
import DanizinhaImg from '../../assets/img/Danizinha_img.png';
import './styled.jsx';
import './markdown-styles.css';

const ChatDanizinha = ({ show, onHide, redacaoId, onInitialize }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMinimized, setIsMinimized] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const messagesEndRef = useRef(null);

  // Hook para interceptar navegação programática
  useNavigationHandler(() => {
    if (isInitialized) {
      console.log('🔄 Navegação programática detectada, desativando sessão...');
      disableSession();
    }
  }, isInitialized);

  // Função para limpar o texto da resposta do ChatGPT (react-markdown fará a formatação)
  const cleanBotMessage = (text) => {
    if (!text) return text;
    
    return text
      // Remove espaços extras no início e fim
      .trim()
      // Normaliza quebras de linha
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Função para desativar a sessão da Danizinha
  const disableSession = async () => {
    try {
      try {
        // Primeira tentativa: com cookies
        await apiDanizinha.get('/desativarSession');
        console.log('✅ Sessão desativada com sucesso (com cookies)');
      } catch (corsError) {
        // Se falhar por CORS, tentar sem cookies
        if (corsError.message.includes('CORS') || corsError.message.includes('Network Error') || 
            (corsError.response && corsError.response.status === 0)) {
          console.log('⚠️ Erro de CORS ao desativar sessão, tentando sem cookies...');
          await api.get('/desativarSession');
          console.log('✅ Sessão desativada com sucesso (sem cookies)');
        } else {
          throw corsError;
        }
      }
    } catch (error) {
      console.error('❌ Erro ao desativar sessão:', error);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Effect para gerenciar o encerramento da sessão quando a página é recarregada, fechada ou navegação interna
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Se o chat estiver inicializado, desativar a sessão
      if (isInitialized) {
        // Para requisições síncronas no beforeunload, usamos navigator.sendBeacon ou fetch com keepalive
        const disableSessionSync = async () => {
          try {
            // Usar fetch com keepalive para garantir que a requisição seja enviada mesmo se a página fechar
            await fetch(`${config.server.hostname}/desativarSession`, {
              method: 'GET',
              credentials: 'include',
              keepalive: true,
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            });
            console.log('✅ Sessão desativada antes do fechamento da página');
          } catch (error) {
            console.error('❌ Erro ao desativar sessão no beforeunload:', error);
          }
        };
        
        disableSessionSync();
      }
    };

    const handlePopState = (event) => {
      // Detecta navegação usando botão voltar/avançar do navegador
      if (isInitialized) {
        console.log('🔙 Navegação detectada (botão voltar/avançar), desativando sessão...');
        disableSession();
      }
    };

    const handleVisibilityChange = () => {
      // Não desativa a sessão apenas por mudança de visibilidade
      // A sessão será mantida até o usuário realmente sair da página ou recarregar
      // Isso preserva a sessão do chatbot quando o usuário muda de aba
      console.log('👁️ Visibilidade da página alterada:', document.visibilityState);
    };

    // Adicionar event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function para remover os event listeners
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Não desativa automaticamente a sessão no cleanup
      // A sessão será mantida para preservar o contexto do chatbot
      // Será desativada apenas no beforeunload ou popstate (navegação)
      console.log('🧹 Limpando event listeners do ChatDanizinha');
    };
  }, [isInitialized]); // Dependência do isInitialized para recriar os listeners quando necessário

  const initializeChat = () => {
    if (!isInitialized) {
      setIsInitialized(true);
      onInitialize && onInitialize();
      // Mensagem inicial da Danizinha
      // setMessages([{
      //   type: 'bot',
      //   text: 'Oi! Eu sou a Danizinha 😊 Tô aqui para te ajudar!',
      //   timestamp: new Date()
      // }, {
      //   type: 'bot',
      //   text: 'Me conta, o que você precisa? É só escolher uma das opções aqui embaixo',
      //   timestamp: new Date()
      // }]);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    // Inicializar o chat se ainda não foi inicializado
    if (!isInitialized) {
      initializeChat();
    }

    const userMessage = {
      type: 'user',
      text: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    setError('');

    try {
      let response;
      
      // Todos os usuários agora usam o endpoint /enviarMensagem
      const endpoint = '/enviarMensagem';
      const requestData = {
        id_redacao: parseInt(redacaoId),
        text_user: inputMessage
      };
      
      console.log('📋 Configuração da requisição:');
      console.log('  - Endpoint:', endpoint);
      console.log('  - Request data:', requestData);
      
      try {
        // Primeira tentativa: com cookies para manter contexto
        console.log('🍪 Tentando enviar com cookies para manter contexto...');
        console.log('  - URL:', config.server.hostname + endpoint);
        console.log('  - withCredentials:', true);
        console.log('  - Cookies atuais:', document.cookie);
        console.log('  - Cookies específicos do domínio:', document.cookie.split(';').filter(c => c.trim()));
        console.log('  - Headers Authorization:', `Bearer ${localStorage.getItem('token')?.substring(0, 20)}...`);
        
        // Verificar se há cookies de sessão específicos
        const sessionCookies = document.cookie.split(';').filter(cookie => 
          cookie.includes('connect.sid') || 
          cookie.includes('JSESSIONID') || 
          cookie.includes('session') ||
          cookie.includes('sess')
        );
        console.log('  - Cookies de sessão encontrados:', sessionCookies);
        
        response = await apiDanizinha.post(endpoint, requestData);
        console.log('✅ Mensagem enviada com sucesso (com cookies)');
        console.log('📨 Response headers:', response.headers);
        console.log('🍪 Cookies após resposta:', document.cookie);
        
        // Verificar se o backend enviou novos cookies
        const setCookieHeader = response.headers['set-cookie'];
        if (setCookieHeader) {
          console.log('🆕 Novos cookies recebidos do backend:', setCookieHeader);
        } else {
          console.log('⚠️ Nenhum cookie novo foi enviado pelo backend');
        }
      } catch (corsError) {
        // Se falhar por CORS, tentar sem cookies
        if (corsError.message.includes('CORS') || corsError.message.includes('Network Error') || 
            (corsError.response && corsError.response.status === 0)) {
          console.log('⚠️ Erro de CORS detectado, tentando sem cookies...');
          
          response = await api.post(endpoint, requestData);
          console.log('✅ Mensagem enviada com sucesso (sem cookies)');
        } else {
          // Se não for erro de CORS, relançar o erro
          throw corsError;
        }
      }
      
      if (isFirstMessage) {
        setIsFirstMessage(false);
      }

      // Verificar se atingiu o limite de mensagens
      const responseText = response.data.resposta;
      const isLimitReached = responseText === "limite de mensagens alcançado";
      
      const botMessage = {
        type: 'bot',
        text: cleanBotMessage(responseText),
        timestamp: new Date(),
        isLimitReached: isLimitReached
      };

      setMessages(prev => [...prev, botMessage]);
      
      // Se atingiu o limite, desabilitar o input
      if (isLimitReached) {
        setIsLimitReached(true);
        setError('Limite de mensagens alcançado.');
      }
    } catch (error) {
      console.error('❌ Erro ao enviar mensagem:', error);
      
      // Verificar se o erro contém a mensagem de limite
      let errorText = 'Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente em alguns instantes.';
      let displayError = 'Erro ao enviar mensagem. Tente novamente.';
      
      if (error.response && error.response.data && error.response.data.resposta) {
        const responseText = error.response.data.resposta;
        if (responseText === "limite de mensagens alcançado" || responseText.toLowerCase().includes('limite')) {
          errorText = 'Você atingiu o limite de mensagens para esta sessão.';
          displayError = 'Limite de mensagens alcançado.';
          setIsLimitReached(true);
        }
      }
      
      setError(displayError);
      
      const errorMessage = {
        type: 'bot',
        text: errorText,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClose = async () => {
    // Desativar a sessão usando a função centralizada
    await disableSession();
    
    // Limpar mensagens e fechar chat
    setMessages([]);
    setError('');
    setIsMinimized(false);
    setIsInitialized(false);
    setIsFirstMessage(true);
    setIsLimitReached(false);
    onHide();
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // const quickOptions = [
  //   'Como melhorar minha nota?',
  //   'Explicar competências',
  //   'Dicas de redação',
  //   'Revisar correção'
  // ];

  if (!show) return null;

  return (
    <S.ChatWidget isMinimized={isMinimized}>
      <S.ChatHeader>
        <S.ChatHeaderContent>
          <S.ChatAvatar>
             <S.AvatarImage src={DanizinhaImg} alt="Danizinha" />
           </S.ChatAvatar>
          <S.ChatInfo>
             <S.ChatTitle>Danizinha</S.ChatTitle>
             <S.ChatStatus>
               <S.OnlineIndicator />
               Online
             </S.ChatStatus>
           </S.ChatInfo>
        </S.ChatHeaderContent>
        <S.ChatControls>
          <S.ControlButton onClick={handleMinimize}>
            <i className={isMinimized ? "fas fa-chevron-up" : "fas fa-minus"}></i>
          </S.ControlButton>
        </S.ChatControls>
      </S.ChatHeader>

      {!isMinimized && (
        <>
          <S.ChatBody>
            <S.MessagesContainer>
              {!isInitialized ? (
                <S.WelcomeContainer>
                  <S.WelcomeAvatar>
                    <S.AvatarImage src={DanizinhaImg} alt="Danizinha" />
                  </S.WelcomeAvatar>
                  <S.WelcomeText>
                    <h4>Oi, eu sou Danizinha!😊</h4>
                    <p>Estou aqui para ajudar você em suas dúvidas sobre a redação.</p>
                    <p>Digite uma mensagem para começarmos a conversar.</p>
                  </S.WelcomeText>
                </S.WelcomeContainer>
              ) : (
                <>
                  {messages.map((message, index) => (
                    <S.MessageWrapper key={index} isUser={message.type === 'user'}>
                      {message.type === 'bot' && (
                         <S.BotAvatar>
                           <S.AvatarImage src={DanizinhaImg} alt="Danizinha" />
                         </S.BotAvatar>
                       )}
                      <S.Message isUser={message.type === 'user'}>
                        <S.MessageContent>
                          {message.type === 'bot' ? (
                            <ReactMarkdown className="chat-markdown">{message.text}</ReactMarkdown>
                          ) : (
                            message.text
                          )}
                        </S.MessageContent>
                      </S.Message>
                    </S.MessageWrapper>
                  ))}
                  
                  {loading && (
                     <S.MessageWrapper isUser={false}>
                       <S.BotAvatar>
                         <S.AvatarImage src={DanizinhaImg} alt="Danizinha" />
                       </S.BotAvatar>
                       <S.Message isUser={false}>
                         <S.TypingIndicator>
                           <span></span>
                           <span></span>
                           <span></span>
                         </S.TypingIndicator>
                       </S.Message>
                     </S.MessageWrapper>
                   )}
                </>
              )}
              
              <div ref={messagesEndRef} />
            </S.MessagesContainer>

            {/* {isInitialized && messages.length <= 2 && (
              <S.QuickOptions>
                {quickOptions.map((option, index) => (
                  <S.QuickOptionButton key={index} onClick={() => setInputMessage(option)}>
                    {option}
                  </S.QuickOptionButton>
                ))}
              </S.QuickOptions>
            )} */}
          </S.ChatBody>

          <S.ChatFooter>
            {error && (
              <Alert variant="danger" className="m-2 mb-0 small">
                {error}
              </Alert>
            )}
            
            <S.InputContainer>
              <S.ChatInput
                placeholder={isLimitReached ? "Limite de mensagens atingido" : "Converse comigo"}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading || isLimitReached}
              />
              <S.SendButton
                onClick={sendMessage}
                disabled={loading || !inputMessage.trim() || isLimitReached}
              >
                {loading ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <i className="fas fa-paper-plane"></i>
                )}
              </S.SendButton>
            </S.InputContainer>
          </S.ChatFooter>
        </>
      )}
    </S.ChatWidget>
  );
};

export default ChatDanizinha;